import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";
import { collectOptionPrimaryComponentBlocks } from "./dtoolsOptionComponentGroups";
import { inferDtoolsEquipmentBenefit } from "./dtoolsEquipmentRoles";
import { isWeakCustomerHighlight } from "./dtoolsCustomerOptionCopy";
import type { DtoolsQuotedItemSummary } from "./dtoolsSystemOverviewQuotedItems";
import { collectPrimaryQuotedItemsForOverview } from "./dtoolsSystemOverviewQuotedItems";

/** Parse AI highlights output into 3–6 concise bullets. */
export function parseHighlightsFromAiText(text: string): string[] {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (Array.isArray(parsed)) {
      return parsed
        .map((entry) => String(entry ?? "").trim())
        .filter(Boolean)
        .slice(0, 6);
    }
  } catch {
    // fall through to line parsing
  }

  return trimmed
    .split("\n")
    .map((line) => line.replace(/^[\s*•✓\-–—]+/, "").trim())
    .filter(Boolean)
    .slice(0, 6);
}

export function buildFallbackSystemHighlights(system: DtoolsReviewSystem): string[] {
  const blocks = collectOptionPrimaryComponentBlocks(system);
  const benefits = blocks
    .map((block) => inferDtoolsEquipmentBenefit(block.line))
    .filter((benefit) => benefit && !isWeakCustomerHighlight(benefit));

  const unique: string[] = [];
  for (const benefit of benefits) {
    if (!unique.some((existing) => existing.toLowerCase() === benefit.toLowerCase())) {
      unique.push(benefit);
    }
    if (unique.length >= 6) break;
  }

  if (unique.length < 3) {
    unique.push("Professional Calibration Ready");
  }

  return unique.slice(0, 6);
}

export { isWeakCustomerHighlight };

export function buildSystemHighlightsGeneratePayload(input: {
  optionName: string;
  items: DtoolsQuotedItemSummary[];
}): string {
  const primaryItems = input.items.filter((item) => !item.isAccessory);
  const focusItems = primaryItems.length > 0 ? primaryItems : input.items;

  const itemLines = focusItems.slice(0, 60).map((item) => {
    const role = item.equipmentRole ? ` (${item.equipmentRole})` : "";
    const brandModel = [item.manufacturer, item.model].filter(Boolean).join(" ").trim();
    const qty = item.quantity > 1 ? ` ×${item.quantity}` : "";
    const label = brandModel || item.description;
    return `- ${label}${qty}${role}`;
  });

  return [
    `Option label: ${input.optionName}`,
    "Write 3–7 concise customer-facing KEY HIGHLIGHTS — each line should describe a BENEFIT or CAPABILITY, not a bare model number.",
    "Rules:",
    "- Derive each highlight from the equipment ROLES and quoted PRIMARY equipment below.",
    "- Good examples: 'Independent music control in multiple areas of the home', 'Native 4K Laser Projection', 'Dolby Atmos Immersive Audio', 'Trinnov Room Optimisation'.",
    "- Bad examples: '6-zone amplifier', '7.1.4', '500W per channel'.",
    "- Avoid listing model numbers unless no meaningful benefit can be inferred from the brief.",
    "- Customer-friendly, factual, no marketing fluff or superlatives.",
    "- Do NOT mention room names, room layout, or equipment closets.",
    "- Do NOT invent features, specifications, or products not listed.",
    "- Each highlight on its own line (no bullets, numbers, or markdown).",
    "Primary quoted equipment (with inferred roles):",
    ...(itemLines.length ? itemLines : ["- (No billable items listed)"]),
  ].join("\n");
}

export function collectPrimaryItemsForHighlights(system: DtoolsReviewSystem): DtoolsQuotedItemSummary[] {
  return collectPrimaryQuotedItemsForOverview(system);
}
